import { SSRMeasures } from '@confluence/action-measures/entry-points/SSRMeasures.ts';
import { ssrPrepareErrorsClient } from '@confluence/ssr-utilities/entry-points/SSRPrepareErrorsClient.ts';
import { type SuspenseableDataFetcher } from '@confluence/query-preloader-boundary/entry-points/getDataFetcherForSuspense.ts';
import { runInQueryBoundary } from '@confluence/query-preloader-boundary/entry-points/queryBoundaryStorage.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

type PromiseState = 'pending' | 'resolved' | 'error';

declare global {
	interface Window {
		__CONFLUENCE_CURRENT_DATA_FETCHER_BOUNDARY__?: string;
	}
}

export const DEFAULT_DATA_FETCHER_TIMEOUT: 5000 | 3000 =
	process.env.CLOUD_ENV === 'staging' ||
	process.env.CLOUD_ENV === 'branch' ||
	process.env.CLOUD_ENV === 'dev'
		? 5000
		: 3000;

class DataFetcherTimeoutError extends Error {}

export function isDataFetcherAllFinished(): boolean {
	if (window.__SSR_REACT_STREAMING__) {
		return Object.values(window.__dataFetcherForSuspense__ || {}).every((dataFetcher) => {
			// All query are put into default category. No Suspense should wait for default.
			return !dataFetcher.isPending();
		});
	}
	return true;
}

/**
 * Same interface as Promise.all but wait for all promises to settle.
 */
export async function allSettled<T>(promises: Promise<T>[]): Promise<T[]> {
	return Promise.allSettled(promises).then((results) => {
		// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity -- Existing helper iterates over bounded dataFetcher result arrays for clear error collection.
		const errors = results
			.filter((result): result is PromiseRejectedResult => result.status === 'rejected')
			.map((result) => result.reason);

		// Keep the same interface as Promise.all putting all other errors in otherErrors field
		if (errors.length > 0) {
			// eslint-disable-next-line no-console
			console.error(`Error caught by createDataFetcherForSuspense`, {
				errors: errors.map((error) => error.message),
			});
			const error = errors[0];
			if (errors.length > 1) {
				error.otherErrors = errors.slice(1);
			}
			throw error;
		}
		// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity -- Existing helper iterates over bounded dataFetcher result arrays for clear fulfilled-value collection.
		return results
			.filter((result): result is PromiseFulfilledResult<any> => result.status === 'fulfilled')
			.map((result) => result.value);
	});
}

export function createDataFetcherForSuspense(
	dataFetcherName: string,
	callback: () => Promise<any>[] | Promise<any> | null,
): Promise<any> {
	let promise: Promise<any> | Promise<any>[] | null = null;
	try {
		runInQueryBoundary(dataFetcherName, () => {
			promise = runWithFrontendDataFetcherBoundary(dataFetcherName, callback);
		});
	} catch (error) {
		promise = Promise.reject(error);
	}

	promise = promise ?? Promise.resolve();

	if (Array.isArray(promise)) {
		promise = allSettled(promise);
	}

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (!process.env.REACT_SSR || !window.__SSR_REACT_STREAMING__) {
		return promise;
	}

	if (!window.__dataFetcherForSuspense__) {
		window.__dataFetcherForSuspense__ = {};
	}

	const dataFetcher = window.__dataFetcherForSuspense__[dataFetcherName];
	if (!dataFetcher) {
		window.__dataFetcherForSuspense__[dataFetcherName] = createDataFetcher(
			dataFetcherName,
			promise,
		);
	}

	return Promise.resolve();
}

function runWithFrontendDataFetcherBoundary<T>(dataFetcherName: string, callback: () => T): T {
	/* eslint-disable check-react-ssr-usage/no-react-ssr -- This helper uses the SSR flag as a non-rendering runtime guard. */
	if (process.env.REACT_SSR || typeof window === 'undefined') {
		return callback();
	}
	/* eslint-enable check-react-ssr-usage/no-react-ssr */

	const previousBoundary = window.__CONFLUENCE_CURRENT_DATA_FETCHER_BOUNDARY__;
	window.__CONFLUENCE_CURRENT_DATA_FETCHER_BOUNDARY__ = dataFetcherName;

	try {
		return callback();
	} finally {
		if (previousBoundary !== undefined) {
			window.__CONFLUENCE_CURRENT_DATA_FETCHER_BOUNDARY__ = previousBoundary;
		} else {
			delete window.__CONFLUENCE_CURRENT_DATA_FETCHER_BOUNDARY__;
		}
	}
}

function guardTimeout(name: string, promise: Promise<any>) {
	const dataFetcherTimeout = StatsigConfigurations.getNumber(
		'confluence_frontend_data_fetcher_timeout',
		DEFAULT_DATA_FETCHER_TIMEOUT,
	);
	return Promise.race([
		promise,
		new Promise((_, reject) =>
			setTimeout(() => {
				reject(
					new DataFetcherTimeoutError(`Data fetcher "${name}" timeout at ${dataFetcherTimeout}ms`),
				);
			}, dataFetcherTimeout),
		),
	]);
}

// export for testing
export function createDataFetcher(
	dataFetcherName: string,
	promise: Promise<any>,
): SuspenseableDataFetcher {
	let dataResult: any = null;
	let errorResult: any = null;
	let promiseState: PromiseState = 'pending';
	const measureName = `ssr-app/prepare/preloadQuery/dataFetcher:${dataFetcherName}`;

	const suspender = new Promise<void>((resolve) => {
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (process.env.REACT_SSR) {
			SSRMeasures.markMeasureStart(measureName);
		}
		guardTimeout(dataFetcherName, promise)
			.then((result) => {
				dataResult = result;
				promiseState = 'resolved';
				// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
				if (process.env.REACT_SSR) {
					SSRMeasures.markMeasureEnd(measureName);
				}
				resolve();
			})
			.catch((error) => {
				errorResult = error;
				promiseState = 'error';
				// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
				if (process.env.REACT_SSR) {
					SSRMeasures.markMeasureEnd(measureName);
				}
				// When encountered multiple errors, errors other than the first one are stored in otherErrors field.
				const errors = [error].concat(error.otherErrors || []);
				for (const err of errors) {
					// eslint-disable-next-line no-console
					console.error(`Data fetcher "${dataFetcherName}" error: ${err.stack || err.message}`);
					// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
					if (process.env.REACT_SSR) {
						ssrPrepareErrorsClient.submitError(err, { dataFetcherName });
					}
				}
				resolve();
			});
	});

	return {
		getName() {
			return dataFetcherName;
		},
		isTimeout() {
			return errorResult instanceof DataFetcherTimeoutError;
		},
		getError() {
			return errorResult;
		},
		isPending() {
			return promiseState === 'pending';
		},
		getPromise() {
			return promise;
		},
		read() {
			if (promiseState === 'pending') {
				throw suspender;
			} else if (promiseState === 'error') {
				// Data fetcher is a wrapper of preloader. If an error happened we don't want to blow up the tree too early.
				// Return null here to signal the Suspense to continue.
				// The normal error handling in the component will take care of it.
				return null;
			} else if (promiseState === 'resolved') {
				return dataResult;
			}
		},
	};
}
